// Import the dynamic free shipping progress bar logic
// import './free-shipping-progress.js';

// Check if CartDrawer is already defined before declaring the class
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
      
      // Add event listener for item removal animations
      this.setupItemRemovalAnimation();
      this.setupItemAdditionAnimation();
    }
    
    // Setup animation for when items are added to the cart
    setupItemAdditionAnimation() {
      document.addEventListener('cart:item-added', (event) => {
        if (event.detail && event.detail.variantId) {
          // Store the variant ID that was just added
          this.lastAddedVariantId = event.detail.variantId;
        }
      });
    }
    
    // Setup animation for cart item removal
    setupItemRemovalAnimation() {
      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('cart-remove-button');
        if (!removeButton) return;
        
        const cartItem = removeButton.closest('.cart-item');
        if (cartItem) {
          // Add a class to animate the item out
          cartItem.classList.add('cart-item--removing');
          
          // Short delay before actual removal to allow animation to play
          const removeClickHandler = removeButton.querySelector('button').onclick;
          removeButton.querySelector('button').onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Allow time for the fade-out animation to play
            setTimeout(() => {
              if (typeof removeClickHandler === 'function') {
                removeClickHandler.call(removeButton.querySelector('button'), e);
              }
            }, 300);
          };
        }
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      
      // Apply staggered animations to cart items
      this.setAnimationOrder();
      
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
          
          // Highlight newly added item if present
          if (this.lastAddedVariantId) {
            this.highlightAddedItem(this.lastAddedVariantId);
            this.lastAddedVariantId = null;
          }
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    }
    
    // Highlight the newly added item
    highlightAddedItem(variantId) {
      if (!variantId) return;
      
      const cartItems = this.querySelectorAll('.cart-item');
      
      cartItems.forEach(item => {
        const quantityInput = item.querySelector('.quantity__input');
        if (quantityInput && quantityInput.dataset.quantityVariantId == variantId) {
          // Add highlight effect
          item.classList.add('cart-item--highlighted');
          
          // Remove the highlight after animation completes
          setTimeout(() => {
            item.classList.remove('cart-item--highlighted');
          }, 2000);
        }
      });
    }

    close() {
      this.classList.remove('active');
      // Remove animate class after transition completes
      this.addEventListener('transitionend', () => {
        this.classList.remove('animate');
      }, { once: true });
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    // Set animation order for staggered fade-in
    setAnimationOrder() {
      const cartItems = this.querySelectorAll('.cart-item');
      cartItems.forEach((item, index) => {
        item.style.setProperty('--animation-order', index);
        
        // Remove any existing animation classes
        item.classList.remove('cart-item--removing');
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        
        // Remove animation classes first to ensure they can be reapplied
        this.classList.remove('animate');
        // Re-add animate class after a small delay to trigger animations again
        setTimeout(() => {
          this.open();
          // Dispatch event for JS interactions to be reinitialized
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }, 10);
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

// Only define CartDrawerItems if CartItems exists and CartDrawerItems is not already defined
if (typeof CartItems !== 'undefined' && !customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }
  
  customElements.define('cart-drawer-items', CartDrawerItems);
}
